import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Wifi, WifiOff, Send, Zap, MapPin } from "@/components/icons";
import { fetchUnit, unitHeartbeat, permitCharging } from "@/lib/api";
import { statusBadgeProps, formatNum } from "@/lib/unit-helpers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/app/AuthContext";
import { formatPower } from "@voltie/localization-web";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";
import { energyFromWh } from "@/app/displayFormat";
import { workModeLabel } from "@/lib/unit-labels";

interface ChargerDetailPanelProps {
  unitId: string;
}

export function ChargerDetailPanel({ unitId }: ChargerDetailPanelProps) {
  const { isAdmin } = useAuth();
  const t = useT();
  const unitQ = useQuery({
    queryKey: ["unit", unitId],
    queryFn: () => fetchUnit(unitId),
    enabled: !!unitId,
    refetchInterval: 5_000,
  });

  const heartbeatM = useMutation({
    mutationFn: () => unitHeartbeat(unitId),
    onSuccess: () => toast.success(t("operator.unit.heartbeat_sent")),
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.unit.heartbeat_failed" })),
  });

  const permitM = useMutation({
    mutationFn: () => permitCharging(unitId),
    onSuccess: () => toast.success(t("operator.unit.permit_sent")),
    onError: (e) => toast.error(errorMessage(t, e, { family: "unit_settings", fallback: "operator.unit.permit_failed" })),
  });

  if (unitQ.isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse rounded-xl bg-muted h-8 w-2/3" />
        <div className="animate-pulse rounded-xl bg-muted h-4 w-1/2" />
        <div className="animate-pulse rounded-xl bg-muted h-32 w-full" />
      </div>
    );
  }

  if (unitQ.error || !unitQ.data) {
    return <div className="text-error">{t("operator.units.detail_load_failed")}</div>;
  }

  const unit = unitQ.data;
  const online = unit.is_online !== false;
  const bp = statusBadgeProps(unit.state_name || "", online);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          {online ? <Wifi className="h-5 w-5 text-success" /> : <WifiOff className="h-5 w-5 opacity-40" />}
          <h2 className="text-xl font-bold">{unit.name}</h2>
        </div>
        <div className="font-mono text-sm opacity-60 mt-1">{unit.unit_id}</div>
        <div className="mt-2">
          <Badge variant={bp.variant} outline={bp.outline}>{online ? unit.state_name || t("operator.units.badge_unknown") : t("operator.units.badge_offline")}</Badge>
        </div>
      </div>

      {/* Location */}
      {(unit.station_name || unit.station_address || unit.location) && (
        <div className="rounded-xl border border-border p-3 space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold opacity-70 uppercase">
            <MapPin className="h-3.5 w-3.5" /> {t("operator.unit.location")}
          </div>
          {unit.station_name && (
            <div className="text-sm font-medium">{unit.station_name}</div>
          )}
          {unit.station_address && (
            <div className="text-xs opacity-70">{unit.station_address}</div>
          )}
          {!unit.station_name && !unit.station_address && unit.location && (
            <div className="text-xs opacity-70">{unit.location}</div>
          )}
          {unit.station_latitude != null && unit.station_longitude != null && (
            <div className="text-xs opacity-50 font-mono">
              {unit.station_latitude.toFixed(5)}, {unit.station_longitude.toFixed(5)}
            </div>
          )}
          {unit.station_time_zone && (
            <div className="text-xs opacity-50">{t("operator.units.station_zone_short", { timezone: unit.station_time_zone })}</div>
          )}
        </div>
      )}

      {/* Telemetry */}
      <div className="grid grid-cols-2 gap-3">
        <TelemetryItem label={t("operator.metric.voltage")} value={`${formatNum(unit.voltage, 1)}V`} />
        <TelemetryItem label={t("operator.metric.current")} value={`${formatNum(unit.current, 1)}A`} />
        <TelemetryItem label={t("operator.metric.power")} value={formatPower(unit.power_kw ?? 0, "kW", "precise")} />
        <TelemetryItem label={t("operator.metric.power_factor")} value={formatNum(unit.power_factor, 2)} />
        <TelemetryItem label={t("operator.metric.session_energy")} value={energyFromWh(unit.session_energy ?? 0) ?? ""} />
        <TelemetryItem label={t("operator.metric.frequency")} value={`${formatNum(unit.frequency, 1)} Hz`} />
        <TelemetryItem label={t("operator.metric.temperature")} value={unit.temperature != null ? `${formatNum(unit.temperature, 1)}\u00B0C` : t("operator.units.value_na")} />
        <TelemetryItem label={t("operator.metric.wifi_rssi")} value={unit.wifi_rssi != null ? `${unit.wifi_rssi} dBm` : t("operator.units.value_na")} />
        <TelemetryItem label={t("operator.unit.work_mode")} value={unit.work_mode ? workModeLabel(t, unit.work_mode) : t("operator.units.value_na")} />
        <TelemetryItem label={t("operator.units.column_protocol")} value={unit.protocol_version != null ? String(unit.protocol_version) : t("operator.units.value_na")} />
        {unit.config_available === false ? (
          <div className="bg-card rounded-lg p-3">
            <div className="text-xs opacity-60">{t("operator.units.current_limit")}</div>
            <div className="text-sm font-semibold mt-0.5 text-red-600">{t("operator.unit.no_data")}</div>
          </div>
        ) : unit.online_current_limit != null ? (
          <TelemetryItem label={t("operator.units.current_limit")} value={`${unit.online_current_limit}A`} />
        ) : null}
        {unit.tou_enabled && unit.effective_current_limit !== undefined && unit.effective_current_limit !== null
          && unit.effective_current_limit < (unit.online_current_limit ?? Infinity) && (
          <div className="rounded border border-yellow-300 bg-yellow-50 px-2 py-1.5 text-xs text-yellow-800">
            {unit.effective_current_limit === 0
              ? t("operator.units.tou_blocked_short")
              : t("operator.units.tou_limited_short", { amps: String(unit.effective_current_limit) })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold opacity-70 uppercase">{t("operator.units.quick_actions")}</h3>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={heartbeatM.isPending}
            onClick={() => heartbeatM.mutate()}
          >
            <Send className="h-3.5 w-3.5" />
            {t("operator.unit.heartbeat")}
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              disabled={permitM.isPending}
              onClick={() => permitM.mutate()}
            >
              <Zap className="h-3.5 w-3.5" />
              {t("operator.unit.permit")}
            </Button>
          )}
        </div>
      </div>

      {/* Link to full detail */}
      <div className="pt-2 border-t border-border">
        <Link
          to={`/unit/${encodeURIComponent(unitId)}`}
          className="text-sm font-medium text-primary hover:underline"
        >
          {t("operator.units.view_full_details")}
        </Link>
      </div>
    </div>
  );
}

function TelemetryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-card rounded-lg p-3">
      <div className="text-xs opacity-60">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
